import { useQuery } from "@tanstack/react-query";

import { IWorkflowsTableContentServerResponse } from "../../@types/Worflows";
import { workflowsService } from "../../services/worflowsService";

export function useWorkflows(hospitalGroupId?: number) {
  const { data, isError, isFetching, refetch, isRefetching } =
    useQuery<IWorkflowsTableContentServerResponse>({
      queryKey: [`workflows-list-${hospitalGroupId}`],
      queryFn: async () => {
        const { data } = await workflowsService.getAllWorflows(hospitalGroupId);

        return data;
      },
      refetchOnMount: true,
    });

  return {
    workflows: data?.workflows,
    isFetching,
    isError,
    refetch,
    isRefetching,
  };
}

export function useWorkflowsSelectOptions() {
  const { data, isError, isFetching, refetch, isRefetching } = useQuery({
    queryKey: [`workflows-select-options`],
    queryFn: async () => {
      // Eleven independent catalogue reads; in parallel they cost one round trip, not eleven
      // (EN-5197 review). Spirometry status and its periods are ungated, so they live here rather
      // than in the builder's ward-conditional block (EN-5089).
      const [
        ruleConditionTriggersServerResponse,
        ruleConditionComparatorsServerResponse,
        responseTypesServerResponse,
        responseTimesServerResponse,
        actionTypesServerResponse,
        surveyScoreConditionComparatorsServerResponse,
        timezonesServerResponse,
        hospitalTimezoneServerResponse,
        dateEventTypesServerResponse,
        spirometryStatusesServerResponse,
        occurrencePeriodsServerResponse,
      ] = await Promise.all([
        workflowsService.getWorflowsRuleConditionTriggers(),
        workflowsService.getWorflowsRuleConditionComparators(),
        workflowsService.getWorflowsResponseTypes(),
        workflowsService.getWorflowsResponseTimes(),
        workflowsService.getWorflowsActionTypes(),
        workflowsService.getWorflowsSurveyScoreConditionComparators(),
        workflowsService.getTimezones(),
        workflowsService.getHospitalTimezone(),
        workflowsService.getWorkflowsDateEventTypes(),
        workflowsService.getWorflowsSpirometryStatuses(),
        workflowsService.getWorflowsOccurrencePeriods(),
      ]);

      return {
        ruleConditionTriggers: ruleConditionTriggersServerResponse.data,
        ruleConditionComparators: ruleConditionComparatorsServerResponse.data,
        surveyScoreConditionComparators:
          surveyScoreConditionComparatorsServerResponse.data,
        responseTypes: responseTypesServerResponse.data,
        responseTimes: responseTimesServerResponse.data,
        actionTypes: actionTypesServerResponse.data,
        timezones: timezonesServerResponse.data,
        hospitalTimezone: hospitalTimezoneServerResponse.data.timezone,
        dateEventTypes: dateEventTypesServerResponse.data,
        spirometryStatuses: spirometryStatusesServerResponse.data,
        occurrencePeriods: occurrencePeriodsServerResponse.data,
      };
    },
    refetchOnMount: true,
  });

  return {
    ruleConditionTriggers: data?.ruleConditionTriggers,
    ruleConditionComparators: data?.ruleConditionComparators,
    surveyScoreConditionComparators: data?.surveyScoreConditionComparators,
    responseTypes: data?.responseTypes,
    responseTimes: data?.responseTimes,
    actionTypes: data?.actionTypes,
    timezones: data?.timezones,
    hospitalTimezone: data?.hospitalTimezone,
    dateEventTypes: data?.dateEventTypes,
    spirometryStatuses: data?.spirometryStatuses,
    occurrencePeriods: data?.occurrencePeriods,
    isFetching,
    isError,
    refetch,
    isRefetching,
  };
}

export function useHasPatientsWithCarerEmail(wardIds: number[]) {
  const key = JSON.stringify(wardIds);

  const { data } = useQuery({
    queryKey: ["has-patients-with-carer-email", key],
    queryFn: async () => {
      if (wardIds.length === 0) return false;
      const response = await workflowsService.hasPatientsWithCarerEmail(
        wardIds
      );
      return response.data;
    },
    enabled: wardIds.length > 0,
  });

  return data ?? true;
}
